import React, { useEffect, useRef } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from './hooks/useAuth.js';
import Home from './pages/Home.jsx';
import ListDetail from './pages/ListDetail.jsx';
import ItemDetail from './pages/ItemDetail.jsx';
import AddItem from './pages/AddItem.jsx';
import JoinList from './pages/JoinList.jsx';

const styles = {
  loadingScreen: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    backgroundColor: 'transparent',
    color: 'var(--tg-theme-text-color, #000)',
    gap: '16px',
  },
  spinner: {
    width: '40px',
    height: '40px',
    border: '3px solid var(--tg-theme-hint-color, #e0e0e0)',
    borderTopColor: 'var(--tg-theme-button-color, #007aff)',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
  },
};

function LoadingScreen() {
  return (
    <div style={styles.loadingScreen}>
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
      <div style={styles.spinner} />
      <span style={{ fontSize: '14px', color: 'var(--tg-theme-hint-color, #8e8e93)' }}>
        Загрузка...
      </span>
    </div>
  );
}

function LandingScreen() {
  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f9d4b8 0%, #f5b99a 50%, #f0a882 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '24px',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    }}>
      <div style={{
        background: 'rgba(255,255,255,0.55)',
        backdropFilter: 'blur(12px)',
        borderRadius: '28px',
        padding: '40px 32px',
        textAlign: 'center',
        maxWidth: '340px',
        width: '100%',
        boxShadow: '0 8px 40px rgba(0,0,0,0.10)',
        position: 'relative',
      }}>
        <span style={{ position: 'absolute', top: '18px', left: '18px', fontSize: '22px' }}>🩷</span>
        <span style={{ position: 'absolute', top: '18px', right: '18px', fontSize: '22px' }}>⭐</span>
        <span style={{ position: 'absolute', bottom: '18px', left: '18px', fontSize: '20px' }}>✨</span>
        <span style={{ position: 'absolute', bottom: '18px', right: '18px', fontSize: '20px' }}>🌙</span>

        <div style={{ fontSize: '64px', marginBottom: '16px', lineHeight: 1 }}>🎁</div>
        <div style={{ fontSize: '36px', fontWeight: '800', color: '#1a1a1a', marginBottom: '8px', letterSpacing: '-1px' }}>
          iWish
        </div>
        <div style={{ fontSize: '15px', color: '#555', lineHeight: 1.5, marginBottom: '32px' }}>
          Создавай списки желаний<br />и делись с близкими
        </div>

        <a
          href="https://t.me/iwishapp_bot/iwish"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            background: 'linear-gradient(90deg, #ff8c42, #ff6b35)',
            color: '#fff',
            textDecoration: 'none',
            padding: '14px 32px',
            borderRadius: '50px',
            fontSize: '16px',
            fontWeight: '700',
            boxShadow: '0 4px 16px rgba(255,107,53,0.4)',
          }}
        >
          🎁 Открыть iWish
        </a>
      </div>
    </div>
  );
}

export default function App() {
  const { isAuthenticated, loading, error, authenticate } = useAuth();
  const navigate = useNavigate();
  const handledStartParam = useRef(null);

  useEffect(() => {
    if (loading || !isAuthenticated) return;
    const startParam = window.Telegram?.WebApp?.initDataUnsafe?.start_param;
    if (startParam?.startsWith('join_') && handledStartParam.current !== startParam) {
      handledStartParam.current = startParam;
      navigate(`/join/${startParam.slice(5)}`);
    }
  }, [isAuthenticated, loading]);

  if (loading) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated && error) {
    return <LandingScreen />;
  }

  if (!isAuthenticated) {
    return <LoadingScreen />;
  }

  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/lists/:id" element={<ListDetail />} />
      <Route path="/items/:id" element={<ItemDetail />} />
      <Route path="/add" element={<AddItem />} />
      <Route path="/join/:token" element={<JoinList />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
